import { InputHTMLAttributes, forwardRef, useId } from "react";
import clsx from "clsx";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, id, className, required, ...props }, ref) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const hintId = hint ? `${inputId}-hint` : undefined;
    const errorId = error ? `${inputId}-error` : undefined;

    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label htmlFor={inputId} className="text-sm font-medium text-ink">
            {label}
            {required && <span className="text-accent"> *</span>}
          </label>
        )}

        <input
          ref={ref}
          id={inputId}
          required={required}
          aria-invalid={Boolean(error)}
          aria-describedby={clsx(hintId, errorId) || undefined}
          className={clsx(
            "h-10 rounded-lg border bg-white px-3 text-sm text-ink placeholder:text-ink/40",
            "transition-[border-color,box-shadow] duration-150 ease-out",
            "focus-visible:border-primary focus-visible:shadow-[0_0_0_3px_rgba(24,39,61,0.12)]",
            error
              ? "border-accent focus-visible:shadow-[0_0_0_3px_rgba(243,86,86,0.12)]"
              : "border-line",
            "disabled:cursor-not-allowed disabled:bg-black/5",
            className
          )}
          {...props}
        />

        {hint && !error && (
          <p id={hintId} className="text-xs text-ink/50">
            {hint}
          </p>
        )}
        {error && (
          <p id={errorId} className="text-xs font-medium text-accent">
            {error}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";
